import { configureStore } from '@reduxjs/toolkit';

import axios from 'axios';
import authReducer, { authTypes } from './slices/auth';
import flowReducer, { flowTypes } from './slices/flow';
import tabReducer, { tabTypes } from './slices/tabs';
import bucketReducer, { bucketTypes } from './slices/buckets';
import documentationReducer, { DocumentationState } from './slices/documentation';
import assistantReducer, { assistantTypes } from './slices/assistant';

export * from './slices';

const apiEnv = window.location.origin.includes('dev') ? 'apidev' : 'api';

export enum StaticRole {
  // do not change this identifier, this represents the admin role
  Admin = 'e0f70db9-d559-459a-9a4c-9c238d006697',
}

const getApiBaseUrl = (includeSuffix = false) => {
  const suffix = includeSuffix ? '/api' : '';

  if (LOCAL) return `http://localhost:3001${suffix}`;

  const isInternalProd = INTERNAL_CLIENT && window.location.origin.includes('prod');
  const isInternalDev = INTERNAL_CLIENT && !isInternalProd;

  if (isInternalDev) return `https://flow-server.query.dev.telnyx.io:433${suffix}`;
  if (isInternalProd) return `https://flow-server.query.prod.telnyx.io:433${suffix}`;

  return `https://${apiEnv}.telnyx.com/v2/flow`;
};

const getLoaderBaseUrl = () => {
  if (LOCAL) return `http://localhost:3002/api`;

  const isInternalProd = INTERNAL_CLIENT && window.location.origin.includes('prod');
  const isInternalDev = INTERNAL_CLIENT && !isInternalProd;

  if (isInternalDev) return `https://flow-loaders.query.dev.telnyx.io:433`;
  if (isInternalProd) return `https://flow-loaders.query.prod.telnyx.io:433`;

  return `https://${apiEnv}.telnyx.com/v2/flow`;
};

export const API_BASE = getApiBaseUrl(true);
export const LOADER_BASE = getLoaderBaseUrl();
export const API_BASE_NO_API = getApiBaseUrl();

export const localAuthHeaders = {
  Authorization: LOCAL_API_KEY ? `Bearer ${LOCAL_API_KEY}` : '',
  'telnyx-ratelimit-user-id': LOCAL_USER_ID,
  'telnyx-ratelimit-org-id': LOCAL_ORG_ID,
  'Content-Type': 'application/json',
};

export const SECRETS_STALE_TIME = 1000 * 60 * 3; // 3 minutes

export const apiClient = axios.create({
  baseURL: API_BASE,
  headers: LOCAL ? localAuthHeaders : { 'Content-Type': 'application/json' },
});

export const loaderClient = axios.create({
  baseURL: LOADER_BASE,
  headers: LOCAL ? localAuthHeaders : { 'Content-Type': 'application/json' },
});

export const baseClient = axios.create({
  baseURL: API_BASE_NO_API,
  headers: LOCAL ? localAuthHeaders : { 'Content-Type': 'application/json' },
});

export const telnyxClient = axios.create({
  baseURL: `https://${apiEnv}.telnyx.com/v2`,
  headers: LOCAL ? localAuthHeaders : { 'Content-Type': 'application/json' },
});

export const getAuthCookie = () => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; flow_auth=`);

  if (parts.length === 2) {
    const cookie = parts.pop()?.split(';').shift();
    if (cookie) return decodeURIComponent(cookie);
    return null;
  }

  return null;
};

telnyxClient.interceptors.request.use(
  (config) => {
    const token = getAuthCookie();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

apiClient.interceptors.request.use(
  (config) => {
    if (INTERNAL_CLIENT) {
      const userId = localStorage.getItem('internalUserId');
      const orgId = localStorage.getItem('internalOrgId');

      config.headers['telnyx-ratelimit-user-id'] = userId;
      config.headers['telnyx-ratelimit-org-id'] = orgId;

      return config;
    }

    const token = getAuthCookie();
    if (token) {
      config.headers['Authorization'] = `Bearer ${LOCAL ? LOCAL_API_KEY : token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // redirects back to login on 401
      console.error('401 Unauthorized: Redirect to /login');
      // window.location.href = '/login';
    }

    return Promise.reject(error);
  }
);

loaderClient.interceptors.request.use(
  (config) => {
    if (INTERNAL_CLIENT) {
      const userId = localStorage.getItem('internalUserId');
      const orgId = localStorage.getItem('internalOrgId');

      config.headers['telnyx-ratelimit-user-id'] = userId;
      config.headers['telnyx-ratelimit-org-id'] = orgId;

      return config;
    }

    const token = getAuthCookie();
    if (token) {
      config.headers['Authorization'] = `Bearer ${LOCAL ? LOCAL_API_KEY : token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

baseClient.interceptors.request.use(
  (config) => {
    if (INTERNAL_CLIENT) {
      const userId = localStorage.getItem('internalUserId');
      const orgId = localStorage.getItem('internalOrgId');

      config.headers['telnyx-ratelimit-user-id'] = userId;
      config.headers['telnyx-ratelimit-org-id'] = orgId;

      return config;
    }

    const token = getAuthCookie();
    if (token) {
      config.headers['Authorization'] = `Bearer ${LOCAL ? LOCAL_API_KEY : token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export interface State {
  auth: authTypes;
  flow: flowTypes;
  tabs: tabTypes;
  buckets: bucketTypes;
  documentation: DocumentationState;
  assistant: assistantTypes;
}

export type AppDispatch = typeof store.dispatch;

const store = configureStore({
  reducer: {
    auth: authReducer,
    flow: flowReducer,
    tabs: tabReducer,
    buckets: bucketReducer,
    documentation: documentationReducer,
    assistant: assistantReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['secrets/setModal', 'documentation/setModal'],
      },
    }),
});

export default store;
